import React, { useEffect, useMemo } from 'react';

import { alpha, Box, Chip, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';
import { useFormikContext } from 'formik';

import FormFieldsTemplate from 'components/FormFieldsTemplate';
import SearchBar from 'components/SearchBar';
import TemplateMessagePreview from 'components/TemplateMessagePreview';
import { useAppDispatch, useAppSelector } from 'store';
import { atendimentosSelector } from 'store/reducers/atendimentos';
import {
  fetchTemplates,
  setTemplateCategory,
  TemplateInterface,
  templatesSelector,
  WppCategoryEnum,
  WppCategoryEnumLabels,
} from 'store/reducers/templates';
import { MessageVariables } from 'utils/validators';

import { TemplateList } from './TemplateList';

const useStyles = makeStyles(theme => ({
  gridContainer: {
    margin: 0,
    width: '100%',
    flex: 1,
  },
  leftPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingRight: theme.spacing(2),
  },
  listContainer: {
    flex: 1,
    minHeight: 0,
  },
  rightPanel: {
    borderLeft: `1px solid ${theme.palette.divider}`,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    width: '100%',
  },
  categoryChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  categoryChip: {
    backgroundColor: alpha(theme.palette.grey[900], 0.05),
    border: `1px solid ${alpha(theme.palette.grey[900], 0.2)}`,
    color: alpha(theme.palette.text.primary, 0.7),
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 400,
  },
  categoryChipSelected: {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    color: theme.palette.primary.main,
    fontWeight: 400,
    fontSize: theme.typography.caption.fontSize,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
    },
    '& .MuiChip-deleteIcon': {
      color: theme.palette.primary.main,
    },
  },
  emptyPreview: {
    color: theme.palette.text.secondary,
    textAlign: 'center',
    fontSize: theme.typography.body2.fontSize,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: theme.spacing(4, 0),
  },
}));

const capitalizeFirst = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();

interface TemplateSelectionProps {
  open: boolean;
  selectedTemplate: TemplateInterface | null;
  searchTerm: string;
  onTemplateSelect: (template: TemplateInterface) => void;
  onChangeSearchTerm: (term: string) => void;
}

export const TemplateSelection: React.FC<TemplateSelectionProps> = ({
  open,
  selectedTemplate,
  searchTerm,
  onChangeSearchTerm,
  onTemplateSelect,
}) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const {
    templates,
    loadingTemplates,
    category: categoryFromRedux,
  } = useAppSelector(templatesSelector);

  const { values } = useFormikContext<{ messageVariables: MessageVariables }>();

  const { atendimentoSelecionado } = useAppSelector(atendimentosSelector);

  const channelId = atendimentoSelecionado?.canal?.id;

  const filteredTemplates = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) return templates;

    return templates.filter(template =>
      template.wppName?.toLowerCase().includes(term),
    );
  }, [templates, searchTerm]);

  const handleReset = () => {
    onChangeSearchTerm('');
  };

  const handleCategorySelect = (category: WppCategoryEnum) => {
    dispatch(setTemplateCategory(category));

    if (channelId) {
      dispatch(fetchTemplates(channelId));
    }
  };

  const handleCategoryClear = () => {
    dispatch(setTemplateCategory(null));

    if (channelId) {
      dispatch(fetchTemplates(channelId));
    }
  };

  useEffect(() => {
    if (open && channelId) {
      dispatch(fetchTemplates(channelId));
    }
  }, [open, channelId, dispatch]);

  return (
    <Grid container className={classes.gridContainer}>
      <Grid item xs={12} md={6} style={{ paddingBottom: 0 }}>
        <Box className={classes.leftPanel}>
          <Box>
            <SearchBar
              placeholder="Pesquisar modelo"
              value={searchTerm}
              onChange={onChangeSearchTerm}
              onCancelSearch={handleReset}
            />
          </Box>

          <Box className={classes.categoryChips}>
            {[
              WppCategoryEnum.MARKETING,
              WppCategoryEnum.UTILITY,
              WppCategoryEnum.AUTHENTICATION,
            ].map(categoryItem => {
              const isSelected = categoryFromRedux === categoryItem;

              return (
                <Chip
                  key={categoryItem}
                  label={capitalizeFirst(WppCategoryEnumLabels[categoryItem])}
                  size="small"
                  clickable={!isSelected}
                  onClick={() =>
                    !isSelected ? handleCategorySelect(categoryItem) : undefined
                  }
                  onDelete={isSelected ? handleCategoryClear : undefined}
                  deleteIcon={
                    isSelected ? <CloseIcon fontSize="small" /> : undefined
                  }
                  className={clsx(
                    classes.categoryChip,
                    isSelected && classes.categoryChipSelected,
                  )}
                  variant={isSelected ? 'default' : 'outlined'}
                />
              );
            })}
          </Box>

          <Box className={classes.listContainer}>
            <TemplateList
              templates={filteredTemplates}
              selectedTemplate={selectedTemplate}
              onTemplateSelect={onTemplateSelect}
              loading={loadingTemplates}
            />
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} md={6} className={classes.rightPanel}>
        {selectedTemplate ? (
          <Box style={{ width: '100%' }}>
            <TemplateMessagePreview
              whatsappTemplateMessage={selectedTemplate}
              messageVariables={values.messageVariables}
            />

            {selectedTemplate?.formFields && (
              <FormFieldsTemplate formFields={selectedTemplate.formFields} />
            )}
          </Box>
        ) : (
          <div className={classes.emptyPreview}>
            Selecione um template para visualizar a mensagem.
          </div>
        )}
      </Grid>
    </Grid>
  );
};
